import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import type { Article, Highlight, HighlightColor } from "../lib/types";
import {
  getArticle,
  listHighlights,
  createHighlight,
  deleteHighlight,
  updateHighlight,
  setProgress,
  markRead,
  archiveArticle,
  favoriteArticle,
  addTagsToArticle,
  tagsForArticle,
} from "../lib/db";
import { navigate } from "../lib/router";
import { formatAge, formatDate } from "../lib/format";
import { buildTextMap, findRange, toDOMRange, anchorFromSelection, type TextMap } from "./anchoring";

const COLORS: HighlightColor[] = ["yellow", "green", "blue", "pink"];

interface Props {
  params: Record<string, string>;
}

export function Reader({ params }: Props) {
  const id = params.id;
  const [article, setArticle] = useState<Article | null>(null);
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toolbar, setToolbar] = useState<{ x: number; y: number } | null>(null);
  const [controlsVisible, setControlsVisible] = useState(true);

  const contentRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<TextMap | null>(null);
  const lastScrollY = useRef(0);
  const lastSave = useRef(0);

  // Load article + highlights.
  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const a = await getArticle(id);
        if (!a) { setError("Article not found."); return; }
        setArticle(a);
        const [hl, t] = await Promise.all([listHighlights(id), tagsForArticle(id)]);
        setHighlights(hl);
        setTags(t.map((x: { name: string }) => x.name));
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // After render: build text map and restore highlights.
  useEffect(() => {
    if (!article?.content_html || !contentRef.current) return;
    // Defer one tick so the DOM is committed.
    const handle = window.setTimeout(() => {
      const root = contentRef.current;
      if (!root) return;
      restoreHighlights(root, highlights);
      mapRef.current = buildTextMap(root);
    }, 0);
    return () => window.clearTimeout(handle);
  }, [article, highlights]);

  // Restore reading position once on load.
  useEffect(() => {
    if (!article) return;
    if (article.reading_progress > 0 && article.reading_progress < 1) {
      const target = article.reading_progress * (document.documentElement.scrollHeight - window.innerHeight);
      window.scrollTo({ top: target, behavior: "instant" as ScrollBehavior });
    }
  }, [article]);

  // Scroll: hide/show controls, throttle progress persistence, auto-mark-read.
  useEffect(() => {
    if (!article) return;
    const onScroll = () => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(1, y / max) : 0;
      setControlsVisible(y < lastScrollY.current - 4 || y < 80);
      lastScrollY.current = y;
      const now = Date.now();
      if (now - lastSave.current > 1500 && progress > 0) {
        lastSave.current = now;
        void setProgress(id, progress);
      }
      if (progress > 0.95 && !article.read_at) {
        void markRead(id, true);
        setArticle((a) => (a ? { ...a, read_at: new Date().toISOString() } : a));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [article, id]);

  // Selection -> highlight toolbar.
  useEffect(() => {
    const onSel = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !contentRef.current) { setToolbar(null); return; }
      const within = contentRef.current.contains(sel.anchorNode);
      if (!within) { setToolbar(null); return; }
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      setToolbar({ x: rect.left + rect.width / 2, y: rect.top + window.scrollY - 48 });
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, []);

  // Keyboard nav.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") navigate("/");
      else if (e.key === "a" && article) { void archiveArticle(id, true); navigate("/"); }
      else if (e.key === "f" && article) {
        const next = !article.favorite;
        setArticle((a) => (a ? { ...a, favorite: next } : a));
        void favoriteArticle(id, next);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [article, id]);

  const createHl = useCallback(async (color: HighlightColor) => {
    const sel = window.getSelection();
    const root = contentRef.current;
    if (!sel || !root) return;
    // Rebuild the map right before anchoring so node boundaries are current.
    const map = buildTextMap(root);
    const result = anchorFromSelection(map, root, sel);
    if (!result) return;
    const hl = await createHighlight({
      article_id: id,
      ...result.anchor,
      color,
      note: null,
    });
    // Wrap the selection in a <mark>.
    const domRange = toDOMRange(map, result.range);
    if (domRange) wrapRange(domRange, color, hl.id);
    setHighlights((prev) => [...prev, hl]);
    setToolbar(null);
    sel.removeAllRanges();
    mapRef.current = buildTextMap(root);
  }, [id]);

  const removeHl = useCallback(async (hlId: string) => {
    const mark = contentRef.current?.querySelector(`mark.hl[data-id="${hlId}"]`);
    if (mark) {
      const parent = mark.parentNode;
      while (mark.firstChild) parent?.insertBefore(mark.firstChild, mark);
      parent?.removeChild(mark);
      parent?.normalize();
    }
    await deleteHighlight(hlId);
    setHighlights((prev) => prev.filter((h) => h.id !== hlId));
    if (contentRef.current) mapRef.current = buildTextMap(contentRef.current);
  }, []);

  const onAddNote = useCallback(async (hlId: string) => {
    const note = prompt("Note for this highlight:") ?? "";
    await updateHighlight(hlId, { note: note || null });
    setHighlights((prev) => prev.map((h) => (h.id === hlId ? { ...h, note: note || null } : h)));
  }, []);

  const onAddTag = useCallback(async () => {
    const input = prompt("Add tags (comma-separated):") ?? "";
    const names = input.split(",").map((s) => s.trim()).filter(Boolean);
    if (!names.length) return;
    await addTagsToArticle(id, names);
    setTags((prev) => Array.from(new Set([...prev, ...names])));
  }, [id]);

  const scrollToHighlight = (hl: Highlight) => {
    const mark = contentRef.current?.querySelector(`mark.hl[data-id="${hl.id}"]`);
    if (mark) {
      mark.scrollIntoView({ behavior: "smooth", block: "center" });
      mark.classList.add("active");
      setTimeout(() => mark.classList.remove("active"), 1500);
    }
  };

  if (loading) return <div class="empty"><div class="spinner" /></div>;
  if (error) return (
    <div class="empty">
      <h2>{error}</h2>
      <p><a href="#/">Back to inbox</a></p>
    </div>
  );
  if (!article) return null;

  return (
    <>
      <div class="reader-header" style={{ opacity: controlsVisible ? "1" : "0", transition: "opacity 0.2s" }}>
        <div class="row" style="margin-bottom: var(--space-3);">
          <a href={article.url} target="_blank" rel="noopener noreferrer" class="pub" style="text-decoration: none;">
            {article.site_name ?? new URL(article.url).hostname}
          </a>
          <div class="grow" />
          <button class="icon ghost" onClick={() => { void favoriteArticle(id, !article.favorite); setArticle({ ...article, favorite: !article.favorite }); }}
            title="Favorite (f)" aria-label="Favorite">{article.favorite ? "★" : "☆"}</button>
          <button class="icon ghost" onClick={() => { void archiveArticle(id, true); navigate("/"); }}
            title="Archive (a)" aria-label="Archive">✓</button>
          <button class="icon ghost" onClick={() => navigate("/")} title="Back (Esc)" aria-label="Back">×</button>
        </div>
        <h1>{article.title ?? article.url}</h1>
        <div class="pub">
          {article.author && <span>{article.author}</span>}
          {article.author && article.published_at && <span> · </span>}
          {article.published_at && <span>{formatDate(article.published_at)}</span>}
          {article.author || article.published_at ? <span> · </span> : null}
          <span>saved {formatAge(article.saved_at)} ago</span>
        </div>
        <div class="row" style="margin-top: var(--space-3); flex-wrap: wrap; gap: var(--space-2);">
          {tags.map((t) => <span class="chip">#{t}</span>)}
          <button class="ghost" onClick={onAddTag} style="min-height: auto; padding: 2px var(--space-2); font-size: var(--fs-12);">+ tag</button>
        </div>
      </div>

      {article.extraction_status === "ready" && article.content_html ? (
        <div ref={contentRef} class="reader" dangerouslySetInnerHTML={{ __html: article.content_html }} />
      ) : article.extraction_status === "failed" ? (
        <div class="banner error">
          Extraction failed{article.last_error ? `: ${article.last_error}` : ""}.{" "}
          <a href={article.url} target="_blank" rel="noopener noreferrer">Open the original</a>.
        </div>
      ) : (
        <div class="empty">
          <div class="spinner" />
          <p class="muted">Extracting article…</p>
        </div>
      )}

      {highlights.length > 0 && (
        <aside class="card" style="margin-top: var(--space-7); max-width: var(--reader-column); margin-left: auto; margin-right: auto;">
          <h2 style="margin-top: 0; font-size: var(--fs-18);">Highlights ({highlights.length})</h2>
          <div class="col">
            {highlights.map((h) => (
              <div key={h.id} class="col" style={{ borderLeft: `3px solid var(--highlight-${h.color})`, paddingLeft: "var(--space-3)", cursor: "pointer" }}
                onClick={() => scrollToHighlight(h)}>
                <blockquote style="margin: 0; font-size: var(--fs-14); color: var(--fg-muted);">{h.exact_text}</blockquote>
                {h.note && <p class="subtle" style="margin: var(--space-2) 0 0; font-size: var(--fs-12);">{h.note}</p>}
                <div class="row" style="margin-top: var(--space-2);">
                  <div class="grow" />
                  <button class="ghost" style="min-height: auto; padding: 2px var(--space-2); font-size: var(--fs-12);"
                    onClick={(e) => { e.stopPropagation(); void onAddNote(h.id); }}>note</button>
                  <button class="ghost" style="min-height: auto; padding: 2px var(--space-2); font-size: var(--fs-12); color: var(--danger);"
                    onClick={(e) => { e.stopPropagation(); void removeHl(h.id); }}>delete</button>
                </div>
              </div>
            ))}
          </div>
        </aside>
      )}

      {toolbar && (
        <div class="hl-toolbar visible" style={{ left: toolbar.x, top: toolbar.y, transform: "translate(-50%, 0)" }}>
          {COLORS.map((c) => (
            <button key={c} class="swatch" data-color={c} aria-label={`Highlight ${c}`}
              onClick={() => void createHl(c)} />
          ))}
        </div>
      )}
    </>
  );
}

// ---------- helpers ----------

function restoreHighlights(root: HTMLElement, highlights: Highlight[]) {
  // Build a text map over the pristine DOM, find each highlight's range in
  // text coordinates, then wrap. Wrapping doesn't change textContent, so the
  // text map stays valid for subsequent findRange calls; only node boundaries
  // shift, so we rebuild the map before each toDOMRange.
  let map = buildTextMap(root);
  for (const hl of highlights) {
    const range = findRange(map, {
      exact_text: hl.exact_text,
      prefix: hl.prefix,
      suffix: hl.suffix,
      start_offset: hl.start_offset,
      end_offset: hl.end_offset,
    });
    if (!range) continue;
    map = buildTextMap(root); // refresh node boundaries before DOM conversion
    const dom = toDOMRange(map, range);
    if (dom) wrapRange(dom, hl.color, hl.id);
  }
}

function wrapRange(range: globalThis.Range, color: HighlightColor, id: string) {
  const contents = range.extractContents();
  const mark = document.createElement("mark");
  mark.className = "hl";
  mark.dataset.color = color;
  mark.dataset.id = id;
  mark.appendChild(contents);
  range.insertNode(mark);
}
